import { ReactElement, useEffect } from 'react';
import moment from 'moment';
import 'moment-timezone';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import { useSearchParams } from 'react-router-dom';
import { Alert, AlertRule, AlertStatus, DeviceType } from '../../constants/telosair-interfaces';
import { useData } from '../../context/data.context';
import {
  getBgClassNameByAlert,
  getCSSClassBasedOnPriority,
  getParameterByName,
  alertValueText
} from '../../_helpers/globalHelper';
import { updateAlert } from '../../services/dashboard-service';
import Button from '../../controls/Button';
import CheckboxControl from '../../controls/Checkbox';
import { getPriorityColor } from '../../_helpers/dashboardHelper';
import { alertStatus } from '../../constants/global-constants';

/**
 * Who an alert's rule emails. Derived from the rule's current settings - the
 * engine doesn't record deliveries - using the engine's own rule: Low priority
 * never emails, and nothing is sent without an address.
 * @param rule - The alert's rule, if it still exists.
 * @returns Addresses, an empty list for "not emailed", or null when the rule is gone.
 */
export function emailRecipients(rule?: AlertRule): string[] | null {
  if (!rule) return null;
  if (rule.priority?.toLowerCase() === 'low') return [];
  return (rule.notification || '')
    .split(/[,;]/)
    .map((address) => address.trim())
    .filter((address) => address.includes('@'));
}

type DeviceRowProps = {
  alert: Alert;
  selected: boolean;
  onChange: () => void;
  expandToday: boolean;
  setExpandToday: (value: boolean) => void;
};

/**
 * A component to display device data in a grid.
 * @param props - Device Row Properties.
 * @returns A React element representing the DeviceRow component.
 */
export default function AlertRow(props: DeviceRowProps): ReactElement {
  const { alert, selected, onChange, expandToday, setExpandToday } = props;
  const {
    parameters,
    alertRules,
    userConfigData,
    updateAlertData,
    userConfigDataV2,
    clientThresholdSettings
  } = useData();

  const [searchParams, setSearchParams] = useSearchParams();
  const rule = alertRules.filter((item) => item.id === alert.alertId);
  const recipients = emailRecipients(rule[0]);
  const id = searchParams.get('id');
  useEffect(() => {
    if (id && id === alert.id) {
      setExpandToday(true);
    } else if (expandToday) {
      setExpandToday(false);
    }
  }, [searchParams, alert]);

  /**
   * Handles onClick alert.
   */
  const onClickExpand = (): void => {
    const alertId = alert.id.toString();
    if (alertId === id) {
      setSearchParams();
      return;
    }
    setSearchParams({ id: alert.id.toString() });
  };

  const parameter = getParameterByName(alert.parameter, parameters);
  const timeZone = userConfigData?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  /**
   * Difference Duration.
   * @param startTime -StartDate.
   * @param endTime - EndDate.
   * @returns -Difference.
   */
  const calculateTimeDifference = (startTime: string, endTime: string): number => {
    const startMoment = moment(startTime);
    const endMoment = moment(endTime);
    const Duration = endMoment.diff(startMoment, 'minutes');
    return Duration;
  };

  /**
   * Handles delete alert function.
   * @param event - Button click event.
   * @param status - Dismiss | resolved.
   */
  const handleUpdateAlert = async (event: any, status: AlertStatus): Promise<void> => {
    try {
      event.stopPropagation();
      const updatedAlert = await updateAlert({ status }, alert.id);
      updateAlertData(updatedAlert.data);
      toast.success('Alert updated successfully');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    }
  };
  const priority = getPriorityColor(
    alert.parameter,
    alert.avgValue,
    clientThresholdSettings,
    userConfigDataV2,
    DeviceType.MIXED
  );

  return (
    <tr
      onClick={(e) => {
        const target = e.target as HTMLElement; // Cast e.target to HTMLElement
        // If the click target is an input or label, prevent row click behavior
        if (target.tagName === 'INPUT' || target.tagName === 'LABEL' || target.tagName === 'SPAN') {
          return; // Do nothing if it's an input or label
        }
        onClickExpand();
      }}
      className={`bg-white dark:bg-container-500 text-gray-500 dark:text-gray-300 border-b border-b-gray-300 dark:border-b-gray-900 cursor-pointer ${
        expandToday && 'border-l-2 border-l-primary-500'
      }`}
    >
      <td className="pl-6">
        <CheckboxControl id={alert.id} checked={selected} onChange={onChange} />
      </td>
      <td className="px-6">
        <div className="items-center gap-3 flex h-16">
          <div
            className={`w-11 h-11 min-h-[2.75rem] min-w-[2.75rem] ${getBgClassNameByAlert(
              rule
            )} rounded-full flex items-center justify-center font-bold text-xs text-gray-200 overflow-hidden`}
          >
            {parameter?.shortName || '-'}
          </div>
          <div>
            <div className="text-gray-300 text-sm font-medium">
              {alert.name}
              {alertValueText(alert) !== null ? (
                <span>
                  {' '}
                  |{' '}
                  <span className={getCSSClassBasedOnPriority(priority, 'text')}>
                    {alertValueText(alert)}{' '}
                    {userConfigDataV2?.config &&
                      userConfigDataV2?.config.unit &&
                      (alert.parameter as any) !== 'sensor_status' &&
                      userConfigDataV2?.config.unit[alert.parameter]}
                  </span>
                </span>
              ) : (
                ''
              )}
              {/* {userConfigDataV2?.config && userConfigDataV2?.config.unit[alert.parameter]} */}
            </div>
            <div className="justify-start items-center inline-flex text-gray-500 text-xs font-normal">
              #{alert.alertId}
            </div>
          </div>
        </div>
      </td>
      {!expandToday && (
        <td className="text-left">
          <div
            className={clsx('py-1 flex justify-center items-center w-20 rounded-md', {
              'bg-warning-600': alert.status?.toLowerCase() === 'pending',
              'bg-success-600': alert.status?.toLowerCase() === 'resolved',
              'bg-error-600': alert.status?.toLowerCase() === 'dismiss'
            })}
          >
            <p className="text-center text-xs text-gray-300 text-medium">
              {alertStatus[alert.status.toLowerCase() as AlertStatus]}
            </p>
          </div>
        </td>
      )}
      <td style={{ textAlign: expandToday ? 'left' : 'center' }} className="py-3 text-sm">
        {moment(alert.timestamp).tz(timeZone).format('HH:mm a')}-{' '}
        {alert.endDate ? moment(alert.endDate).tz(timeZone).format('HH:mm a') : 'Ongoing'}
        <div className="text-xs text-gray-500 mt-1">
          {moment(alert.timestamp).tz(timeZone).format('DD-MM-YYYY')}
        </div>
      </td>
      {!expandToday && (
        <td className="py-3 text-center">
          {alert.endDate ? (
            <div className="text-xs text-gray-500">
              {calculateTimeDifference(
                moment(alert.timestamp).tz(timeZone).format(),
                moment(alert.endDate).tz(timeZone).format()
              )}{' '}
              mins
            </div>
          ) : (
            '-'
          )}
        </td>
      )}
      {!expandToday && (
        <td className="py-3 text-center">
          <div>
            <div className="text-sm">#{alert.serialNumber}</div>
          </div>
        </td>
      )}
      {!expandToday && (
        <td className="py-3 text-center" title={recipients?.join(', ') || undefined}>
          {recipients === null && <span className="text-gray-500">-</span>}
          {recipients?.length === 0 && <span className="text-xs text-gray-500">No</span>}
          {recipients && recipients.length > 0 && (
            <div className="text-xs">
              <span className="text-success-400">Yes</span>
              <div className="text-gray-500 mt-1 max-w-[10rem] truncate mx-auto">
                {recipients[0]}
                {recipients.length > 1 ? ` +${recipients.length - 1}` : ''}
              </div>
            </div>
          )}
        </td>
      )}
      {!expandToday && (
        <td className="py-3 text-center px-6 max-w-[15%] ">
          <div className="text-gray-500 text-sm font-medium">
            {(!alert?.tags || alert?.tags?.length === 0) && '-'}
            {alert?.tags?.slice(0, 2)?.map((a) => (
              <div
                className="inline-flex items-center gap-1 bg-gray-800 mx-1 py-1 px-2 rounded text-xs text-primary-200"
                key={a}
              >
                {a}
              </div>
            ))}
            {alert?.tags && alert?.tags?.length > 2 && (
              <div>
                <br />
                {`+${alert.tags.length - 2} more`}
              </div>
            )}
          </div>
        </td>
      )}

      <td colSpan={10} className="py-3 text-right">
        {alert.status === 'pending' && (
          <div className="text-gray-500 mr-3 flex gap-2 justify-end">
            {expandToday ? (
              <Button
                onClick={(e) => handleUpdateAlert(e, 'dismiss')}
                name="Dismiss"
                size="sm"
                theme="secondary-gray"
              />
            ) : (
              <button
                onClick={(e) => handleUpdateAlert(e, 'dismiss')}
                type="button"
                aria-label="close"
                className="border border-gray rounded-md m-2 text-error-400"
              >
                <span className="material-icons-outlined text-md m-1.5 cursor-pointer">close</span>
              </button>
            )}
            {expandToday ? (
              <Button
                onClick={(e) => handleUpdateAlert(e, 'resolved')}
                name="Mark as Resolved"
                size="sm"
                theme="primary"
              />
            ) : (
              <button
                onClick={(e) => handleUpdateAlert(e, 'resolved')}
                type="button"
                aria-label="complete"
                className="border border-gray rounded-md m-2 text-success-500"
              >
                <span className="material-icons text-md m-1.5 cursor-pointer">check</span>
              </button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}
